import React from "react";
import { Edit2, Trash2, Eye } from "lucide-react";

export default function VoucherTable({ vouchers, loading, onEdit, onDelete, onView }) {
  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 overflow-hidden">
        <div className="p-8 text-center">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-slate-100 mb-4">
            <div className="h-5 w-5 bg-slate-200 rounded-full animate-pulse" />
          </div>
          <p className="text-slate-600">Loading vouchers...</p>
        </div>
      </div>
    );
  }

  if (!vouchers || vouchers.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center">
        <p className="text-slate-600">No vouchers found</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-900 uppercase">Code</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-900 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-900 uppercase">Offer</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-900 uppercase">Market</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-900 uppercase">Valid</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-900 uppercase">Usage</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-900 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-slate-900 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {vouchers.map((voucher) => (
              <tr key={voucher._id} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-4 text-sm font-semibold text-slate-900">{voucher.code}</td>
                <td className="px-6 py-4 text-sm text-slate-600">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {voucher.type === "fixed"
                      ? "Fixed"
                      : voucher.type === "percent"
                      ? "Percentage"
                      : "Free Shipping"}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-slate-600">
                  {voucher.type === "percent"
                    ? `${voucher.value}%`
                    : voucher.type === "free_shipping"
                      ? "Free shipping"
                      : `${voucher.currency || "USD"} ${Number(voucher.value || 0).toFixed(2)}`}
                  {voucher.maxDiscount && ` (max ${voucher.currency || "USD"} ${Number(voucher.maxDiscount).toFixed(2)})`}
                </td>
                <td className="px-6 py-4 text-xs text-slate-600">
                  <p className="font-medium text-slate-800">
                    {voucher.currencyMode === "any" ? "Any currency" : voucher.currency || "USD"}
                  </p>
                  <p className="mt-0.5 max-w-40 truncate">
                    {voucher.conditions?.validCountries?.length
                      ? voucher.conditions.validCountries.join(", ")
                      : "All enabled countries"}
                  </p>
                </td>
                <td className="px-6 py-4 text-sm text-slate-600">
                  <div className="text-xs">
                    <p>
                      {new Date(voucher.startDate).toLocaleDateString()} -
                      <br />
                      {new Date(voucher.endDate).toLocaleDateString()}
                    </p>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-slate-600">
                  <div>
                    <p className="font-medium">{voucher.totalUsed}</p>
                    {voucher.conditions.totalUsageLimit && (
                      <p className="text-xs text-slate-500">
                        / {voucher.conditions.totalUsageLimit}
                      </p>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      voucher.isActive
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {voucher.isActive ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onView(voucher)}
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                      title="View"
                    >
                      <Eye size={16} className="text-slate-600" />
                    </button>
                    <button
                      onClick={() => onEdit(voucher)}
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit2 size={16} className="text-slate-600" />
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm("Are you sure you want to delete this voucher?")) {
                          onDelete(voucher._id);
                        }
                      }}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={16} className="text-red-600" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
